// Oike — "Agregar al calendario": a Google Calendar event template that opens in
// the user's own calendar, where they confirm it. Oike does not write to any
// calendar (there is no connection here): it only builds the link. Pure.
// Used by Contactos ("Agendar reunión") and by the sessions of Acompañamiento.

const utc = (iso) => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');

/** The link, or null without a valid start. start: ISO instant; minutes: duration. */
export function googleCalendarUrl({ title, start, minutes = 60, details = '', guests = [] }) {
  const t = Date.parse(start || '');
  if (!Number.isFinite(t)) return null;
  const end = new Date(t + Math.max(5, Number(minutes) || 60) * 60000).toISOString();
  const add = guests.filter(Boolean).map(g => `&add=${encodeURIComponent(g)}`).join('');
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title || '')}&dates=${utc(start)}/${utc(end)}&details=${encodeURIComponent(details)}${add}`;
}

/** A local date (YYYY-MM-DD) and time (HH:MM) of this browser as an ISO instant. */
export function localInstant(date, time) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '') || !/^\d{2}:\d{2}$/.test(time || '')) return null;
  const d = new Date(`${date}T${time}`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** An ISO instant as this browser's local date and time, to fill the fields. */
export function localParts(iso) {
  const d = new Date(iso || '');
  if (Number.isNaN(d.getTime())) return { date: '', time: '' };
  const p = (n) => String(n).padStart(2, '0');
  return { date: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`, time: `${p(d.getHours())}:${p(d.getMinutes())}` };
}

// ── Guests of a group session (Acompañamiento › a team session › «Agendar y agregar al calendario») ──
// Only the people who still take part in that process (never everyone at the company), with the email of their card
// today. Repeated emails go once; who has no valid email is said, never guessed. Whoever hires or leads is offered
// apart and unselected (only if they do not take part): the coach chooses them explicitly. Oike only builds the link;
// the coach reviews, saves and sends the invitation in Google Calendar.

const EMAIL_RE = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]{2,}$/;
export const validEmail = (v) => EMAIL_RE.test(String(v || '').trim());

/** { guests: [{ key, names, email, kind, selected }], missing: [{ key, name, reason }], lead } from the process view. */
export function sessionGuests({ members = [], sponsor = null } = {}) {
  const guests = [];
  const missing = [];
  const byEmail = new Map();
  const active = members.filter(m => m && m.status !== 'left');
  for (const m of active) {
    const name = m.person?.name || 'Participante';
    const email = String(m.person?.email || '').trim();
    if (!validEmail(email)) { missing.push({ key: `m:${m.id}`, name, reason: email ? 'invalid' : 'none' }); continue; }
    const k = email.toLowerCase();
    if (byEmail.has(k)) { byEmail.get(k).names.push(name); continue; }
    const g = { key: `m:${m.id}`, names: [name], email, kind: 'member', selected: true };
    byEmail.set(k, g);
    guests.push(g);
  }
  let lead = null;
  const takesPart = sponsor?.personId && active.some(m => m.person?.id === sponsor.personId);
  if (sponsor?.personId && !takesPart) {
    const email = String(sponsor.email || '').trim();
    if (!validEmail(email)) lead = { key: 'sponsor', names: [sponsor.name || 'Quien contrata o lidera'], email: '', kind: 'sponsor', selected: false, reason: email ? 'invalid' : 'none' };
    else if (!byEmail.has(email.toLowerCase())) lead = { key: 'sponsor', names: [sponsor.name || 'Quien contrata o lidera'], email, kind: 'sponsor', selected: false };
  }
  return { guests, missing, lead };
}

/** The emails of the chosen guests, each once, in order. */
export function chosenEmails(list = [], chosen = new Set()) {
  const out = [];
  for (const g of list) if (g && g.email && chosen.has(g.key) && !out.some(e => e.toLowerCase() === g.email.toLowerCase())) out.push(g.email);
  return out;
}
